import { useAutoAnimate } from '@formkit/auto-animate/react';
import { FormattedMessage, useIntl } from 'react-intl';

import type {
  ExtractedFiltersEnabledFragment,
  JobSearchFilterCollectionFragment,
} from '@xing-com/crate-jobs-graphql';
import { useResetFilters, useSearchState } from '@xing-com/crate-jobs-hooks';
import { IconReturn } from '@xing-com/icons';
import { motionTimeNumberL } from '@xing-com/tokens';

import { useSearch } from '../../../hooks';

import * as S from './applied-filters.styles';
import {
  CompanyTag,
  ExtractedLocationTag,
  FilterTag,
  LocationTag,
  SalaryTag,
  SinceTag,
  TimeFrameTag,
} from './tags';

export type EntityIdFilterOption = Extract<
  JobSearchFilterCollectionFragment,
  { entityId: string }
>;

const isEntityIdOption = (
  option: JobSearchFilterCollectionFragment | ExtractedFiltersEnabledFragment
): option is EntityIdFilterOption => {
  if (
    option.__typename === 'JobFilterBenefit' ||
    option.__typename === 'JobFilterBenefitEmployeePerk' ||
    option.__typename === 'JobFilterBenefitWorkingCulture' ||
    option.__typename === 'JobFilterProjob' ||
    option.__typename === 'JobFilterRemote' ||
    option.__typename === 'JobFilterPublishToCompany' ||
    option.__typename === 'JobFilterSalary' ||
    option.__typename === 'JobFilterPaidOnly'
  ) {
    return false;
  }

  return true;
};

type Props = {
  className?: string;
  isSmallScreen?: boolean;
};

export const AppliedFilters: React.FC<Props> = ({
  className,
  isSmallScreen,
}) => {
  const { formatMessage } = useIntl();
  const { filterCollection: selectedFilters, extractedFilters } = useSearch();
  const appliedFilters = [
    ...selectedFilters,
    ...extractedFilters.map((filter) => {
      if (!filter) return filter;
      if (
        filter.__typename === 'JobFilterSalary' &&
        typeof filter.min !== 'number' &&
        typeof filter.max !== 'number'
      ) {
        return false;
      }

      return { ...filter, isExtracted: true };
    }),
  ].filter(Boolean);

  const { searchQuery, sincePeriod, since } = useSearchState();
  const onResetFilters = useResetFilters({
    context: 'serp_all_filters',
    extractedFilters,
  });

  const [animationContainer] = useAutoAnimate({
    duration: Number(motionTimeNumberL),
  });

  if (
    (appliedFilters.length === 0 ||
      // Hide the applied filters if the hidden `paidOnly` filter is the only one active
      (appliedFilters.length === 1 && searchQuery.filter?.paidOnly)) &&
    !searchQuery.location?.text &&
    !sincePeriod &&
    !since
  ) {
    return null;
  }

  let hasCompanyFilter = false;

  return (
    <S.TagContainer
      id={isSmallScreen ? 'applied-filters' : 'applied-filters-sm'}
      data-testid="applied-filters"
      className={className}
      ref={animationContainer}
      aria-label={formatMessage({
        id: 'JOBS_SEARCH_APPLIED_FILTERS_TITLE_A11Y',
      })}
    >
      <LocationTag />
      <SinceTag />
      <TimeFrameTag />
      {appliedFilters.map((option) => {
        if (!option) {
          return null;
        }

        if (option.__typename === 'JobFilterSalary') {
          return <SalaryTag option={option} key="salary" />;
        }

        if (option.__typename === 'JobFilterCompany') {
          const Tag = hasCompanyFilter ? null : <CompanyTag key="company" />;
          hasCompanyFilter = true;
          return Tag;
        }

        if (option.__typename === 'JobFilterLocation') {
          return (
            <ExtractedLocationTag
              option={option}
              key={`${option.__typename}-${option.location?.localizationValue}`}
            />
          );
        }

        if (isEntityIdOption(option)) {
          return (
            <FilterTag
              option={option}
              key={`${option.__typename}-${option.entityId}`}
            />
          );
        }

        return null;
      })}
      <S.ResetButton
        icon={IconReturn}
        onClick={onResetFilters}
        variant="tertiary"
        size="small"
      >
        <FormattedMessage id="JOBS_SEARCH_RESET_FILTERS_BUTTON" />
      </S.ResetButton>
    </S.TagContainer>
  );
};
